import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, FileText, Trash2, Loader2, CheckCircle2, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API, useApp } from "@/state/AppContext";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "urgent", label: "Dringend" },
  { key: "todo", label: "Zu erledigen" },
  { key: "done", label: "Erledigt" },
];

const badge = (u) => u === "red" ? { c: "bg-red-100 text-red-700", d: "🔴", l: "Dringend" }
  : u === "yellow" ? { c: "bg-amber-100 text-amber-700", d: "🟡", l: "Aktion erforderlich" }
  : { c: "bg-emerald-100 text-emerald-700", d: "🟢", l: "Keine Aktion" };

export default function LettersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, authHeaders, setCurrentResult } = useApp();
  const [docs, setDocs] = useState([]);
  const initialFilter = FILTERS.find(f => f.key === searchParams.get("filter")) ? searchParams.get("filter") : "all";
  const [filter, setFilter] = useState(initialFilter);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    if (!user) {
      // load mock demo for logged-out users so UI is testable
      axios.get(`${API}/mock-documents`).then(r => setDocs(r.data));
      return;
    }
    setLoading(true);
    axios.get(`${API}/documents`, { headers: authHeaders })
      .then(r => setDocs(r.data)).finally(() => setLoading(false));
  }, [user, authHeaders]);

  const filtered = docs.filter(d => {
    const a = d.analysis || {};
    const isDone = d.status === "done";
    if (filter === "urgent" && (isDone || a.urgency !== "red")) return false;
    if (filter === "todo" && (isDone || (d.status !== "todo" && a.urgency !== "yellow"))) return false;
    if (filter === "done" && !isDone) return false;
    if (q) {
      const blob = `${a.sender || ""} ${a.document_type || ""} ${a.subject || ""}`.toLowerCase();
      if (!blob.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const openDoc = (d) => {
    setCurrentResult(d);
    navigate("/result");
  };

  const confirmDelete = async () => {
    if (!toDelete || !user) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/documents/${toDelete.id}`, { headers: authHeaders });
      setDocs(docs.filter(x => x.id !== toDelete.id));
      toast.success("Brief gelöscht.");
      setToDelete(null);
    } catch (e) {
      toast.error("Löschen fehlgeschlagen.");
    } finally { setDeleting(false); }
  };

  const toggleDone = async (d) => {
    if (!user || !d.id) return;
    const next = d.status === "done" ? "todo" : "done";
    setTogglingId(d.id);
    try {
      await axios.patch(`${API}/documents/${d.id}/status`, { status: next }, { headers: authHeaders });
      setDocs(docs.map(x => x.id === d.id ? { ...x, status: next } : x));
      toast.success(next === "done" ? "Als erledigt markiert." : "Wieder als offen markiert.");
    } catch (e) {
      toast.error("Aktualisieren fehlgeschlagen.");
    } finally { setTogglingId(null); }
  };

  return (
    <div className="px-5 pt-6 pb-8" data-testid="letters-page">
      <h1 className="text-2xl font-bold text-slate-900">Meine Briefe</h1>
      {!user && (
        <div className="mt-3 rounded-2xl bg-blue-50 border border-blue-100 p-3 text-sm text-blue-900" data-testid="letters-demo-notice">
          Melde dich an, um deine echten Briefe zu speichern. Unten siehst du Beispiele.
        </div>
      )}

      <div className="mt-4 relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="letters-search" value={q} onChange={e => setQ(e.target.value)}
          placeholder="Suchen …" className="pl-9 h-11 rounded-2xl bg-blue-50 border-blue-100" />
      </div>

      <div className="mt-3 flex gap-2 overflow-x-auto hide-scrollbar">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${filter === f.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {loading && <div className="text-slate-500 text-sm">Lade …</div>}
        {!loading && filtered.length === 0 && (
          <div className="rounded-3xl bg-blue-50/60 border border-blue-100 p-6 text-center">
            <FileText className="w-8 h-8 text-blue-600 mx-auto" />
            <p className="mt-2 text-slate-700">Noch keine Briefe.</p>
            <button onClick={() => navigate("/upload")} className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold" data-testid="letters-cta-upload">
              Ersten Brief prüfen
            </button>
          </div>
        )}
        {filtered.map((d, i) => {
          const a = d.analysis || {};
          const b = badge(a.urgency);
          const canEdit = !!user && !!d.id;
          const isDone = d.status === "done";
          const isToggling = togglingId === d.id;
          return (
            <div key={d.id || i} className={`relative rounded-3xl border shadow-sm transition ${isDone ? "bg-emerald-50/60 border-emerald-200" : "bg-white border-slate-200"}`} data-testid={`letter-card-${i}`}>
              <button onClick={() => openDoc(d)} data-testid={`letter-item-${i}`}
                className="w-full text-left p-4 pr-24 active:scale-[0.99] transition">
                <div className="flex items-center justify-between gap-2">
                  <div className={`font-semibold truncate ${isDone ? "text-emerald-900" : "text-slate-900"}`}>
                    {a.sender || "Unbekannt"}
                  </div>
                  {isDone ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium shrink-0 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Erledigt
                    </span>
                  ) : (
                    <span className={`text-xs px-2 py-1 rounded-full ${b.c} font-medium shrink-0`}>{b.d} {b.l}</span>
                  )}
                </div>
                {a.document_type && <div className={`text-sm mt-1 ${isDone ? "text-emerald-800/70" : "text-slate-500"}`}>{a.document_type}</div>}
                {a.deadline && <div className={`text-xs mt-1 ${isDone ? "text-emerald-800/60" : "text-slate-500"}`}>Frist: {new Date(a.deadline).toLocaleDateString("de-DE")}</div>}
              </button>
              {canEdit && (
                <div className="absolute right-2 top-2 flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleDone(d); }}
                    disabled={isToggling}
                    data-testid={`letter-done-${i}`}
                    aria-label={isDone ? "Als offen markieren" : "Als erledigt markieren"}
                    title={isDone ? "Als offen markieren" : "Als erledigt markieren"}
                    className={`p-2 rounded-full transition ${isDone ? "text-emerald-600 hover:bg-emerald-100" : "text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"} disabled:opacity-50`}
                  >
                    {isToggling ? <Loader2 className="w-4 h-4 animate-spin" />
                      : isDone ? <RotateCcw className="w-4 h-4" />
                      : <CheckCircle2 className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setToDelete(d); }}
                    data-testid={`letter-delete-${i}`}
                    aria-label="Brief löschen"
                    className="p-2 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={o => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Brief wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.analysis?.sender || "Dieser Brief"} wird dauerhaft aus deinem Archiv entfernt. Das kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
